import React, { useState, useEffect, useMemo, useRef } from "react";
import { Polygon, Polyline, Marker, InfoBox, Circle } from "@react-google-maps/api";
import { compose } from "recompose";
import { connect, useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import {
  setExploreActiveEditingObject,
  setExploreFocusedObjectId,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  appNotify
} from 'store/actions';
import DistanceManager from "components/explore/gmap/DistanceManager.js";
import { reconstructDataForObject, getGeoFromOverlay  } from "components/explore/gmap/MapDrawingManager";
import { Constants } from 'utilities/constants';
import {
  areEqual,
  convertMetersToLatitude,
  getMilesFromMeters,
  getDiameterFromRadius,
  getCircumferenceFromRadius,
  getAreaFromRadius,
} from 'utilities';
import { formatValue } from "utilities/format";

const useStyles = makeStyles(theme => ({
  propCount: {
    // fontSize: label.fontSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  popupBubble: {
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'translate(-50%, -100%)',
    /* Style the bubble. */
    backgroundColor: 'white',
    padding: 5,
    borderRadius: 5,
    fontFamily: 'sans-serif',
    overflowY: 'auto',
    maxHeight: 60,
    boxShadow: '0px 2px 10px 1px rgba(0, 0, 0, 0.5)',
    whiteSpace: 'nowrap'
  },
  popupBubbleAnchor: {
    position: "absolute",
    width: "100%",
    bottom: 8,
    left: 0,
    "&::after": {
      // content: '""',
      // position: "absolute",
      // top: 0,
      // left: 0,
      // /* Center the tip horizontally. */
      // transform: "translate(-50%, 0)",
      // /* The tip is a https://css-tricks.com/snippets/css/css-triangle/ */
      // width: 0,
      // height: 0,
      // /* The tip is 8px high, and 12px wide. */
      // borderLeft: "6px solid transparent",
      // borderRight: "6px solid transparent",
      // borderTop: "8px solid white"
      top: '100%',
      border: 'solid transparent',
      content: '""',
      height: 0,
      width: 0,
      position: 'absolute',
      pointerEvents: 'none',
      borderColor: 'transparent',
      borderBottomColor: 'white',
      borderLeftColor: 'white',
      borderWidth: '3px',
      marginLeft: '2px',
      boxShadow: '-6px 6px 1px -4px rgba(0,0,0,0.2), -5px 5px 2px -1px rgba(0,0,0,0.14), -4px 4px 5px 0px rgba(0,0,0,0.12)',
      transform: 'translate(-100%) rotate(-45deg) ',
      transformOrigin: '0 0'
    }
  },
  boxWrapper: {
    backgroundColor: '#FFF',
    padding: '0.6rem',
    font: '400 12px Roboto,Arial,sans-serif',
    transform: 'translateY(-6.7rem)',
    borderRadius: '0.125rem',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    lineHeight: 1.4
  },
  titleFontWeight: {
    fontWeight: 700,
  },
  titleMargin: {
    marginBottom: '2px !important',
  },
  commonPosition: {
    transform: 'translate(-50%, -118%) !important',
  },
  boxWrapperWithMeasuring: {
    transform: 'translate(-50%, -108%) !important',
  },
  labelBox: {
    backgroundColor: '#FFF',
    padding: '0.6rem',
    font: '700 12px Roboto,Arial,sans-serif',
    transform: 'translateY(-3rem)',
    borderRadius: '0.125rem',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    lineHeight: 1.4
  },
  pinLabelBox: {
    transform: 'translate(-50%, -205%)',
    overflowY: 'auto',
    maxHeight: 60,
  },
  commonMeasuringBox: {
    transform: 'translate(-50%, -107%)',
    backgroundColor: '#FFF',
    padding: '0.6rem',
    font: '400 12px Roboto,Arial,sans-serif',
    borderRadius: '0.125rem',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    lineHeight: 1.4
  },
  areaMiles: {
    display: 'inline-block',
    fontSize: '0.5rem',
    transform: 'translate(0.125rem,-0.25rem)',
  },
  labelWrapper: {
    maxWidth: '13rem',
    padding: '0.4rem',
    borderRadius: '0.125rem',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
  },
}));

function getPolygonOptions(mapType, isFocused) {
  const mapViewPolygonOptions = {
    fillColor: isFocused ? '#ffa300' : `#2b579e`,
    fillOpacity: 0.15,
    strokeWeight: isFocused ? 5 : 3,
    strokeColor: isFocused ? '#ffa300' : `#2b579e`,
    clickable: true,
    zIndex: 1000
  }
  const satelliteViewPolygonOptions = {
    fillColor: isFocused ? '#ffa300' : `#b1b100`,
    fillOpacity: 0.15,
    strokeWeight: 3,
    strokeColor: `#e4e400`,
    clickable: true,
    zIndex: 1000
  }
  return mapType === 'satellite' || mapType === 'hybrid' ? satelliteViewPolygonOptions : mapViewPolygonOptions;
}

function CustomShape({
  shape,
  mapType,
  isEditing,
  otherObjectIsEditing,
  isFocused,
  isHighlighted,
  onLoad,
  onUnmount,
  visible,
  onClick,
  description,
  isTitleVisible,
}) {
  const classes = useStyles();
  const { labelBox, commonPosition } = classes;

  const coords = shape.coords;
  if (coords) {
    const polygonOptions = getPolygonOptions(mapType, isFocused);

    // set the infobox at the highest latitude position
    function getInfoBoxPosition(a, b) {
      return a.lat > b.lat ? a : b;
    }

    function onLoadInfoBox(infoBox) {
      infoBox.boxClass = infoBox.boxClass + ' infoBoxContainer';
    }

    return (
      <>
        <Polygon
          onLoad={onLoad}
          onUnmount={onUnmount}
          paths={coords}
          editable={isEditing}
          draggable={isEditing}
          options={{
            ...polygonOptions,
            clickable: !otherObjectIsEditing,
            fillColor: isHighlighted ? '#fff700' : isFocused || isEditing ? '#ffa300' : (shape.color ?? polygonOptions.fillColor),
            fillOpacity: isHighlighted || isFocused || isEditing ? 0.3 : (shape.fillOpacity ?? polygonOptions.fillOpacity),
            strokeColor:  isHighlighted ? '#fff700' : isFocused || isEditing ? '#ffa300' : (shape.color ?? polygonOptions.strokeColor),
            strokeWeight: (isHighlighted || isFocused ? 10 : 3) * shape.size
          }}
          onClick={onClick}
          visible={visible}
        />
        {
          isTitleVisible !== undefined && !isEditing
          ? (<InfoBox
              position={coords.reduce(getInfoBoxPosition)}
              visible={isTitleVisible && visible}
              onLoad={onLoadInfoBox}
              options={{
                disableAutoPan: true
              }}
            >
              <div className={`${labelBox} ${commonPosition}`}>{description}</div>
            </InfoBox>)
          : null
        }
      </>
    )
  } else {
    return null;
  }
}

function CircleShape({
  shape,
  mapType,
  isEditing,
  otherObjectIsEditing,
  isFocused,
  isHighlighted,
  onLoad,
  onUnmount,
  visible,
  onClick,
  setRadius,
  manualRadius,
  isTitleVisible,
  description,
  isMeasuringVisible,
}) {
  const classes = useStyles();
  const [instance, setInstance] = useState(null);
  const coords = shape.coords;

  useEffect(() => {
    if (instance && isEditing) {
      instance.setRadius(manualRadius);
    }
  }, [manualRadius]);

  useEffect(() => {
    if (instance && coords) {
      if (instance.getRadius() !== coords.radius) { // handling for when user cancels an edit- restore the original radius
        instance.setRadius(coords.radius);
      }
    }
  }, [coords]);

  const {
    commonMeasuringBox,
    commonPosition,
    boxWrapperWithMeasuring,
    titleFontWeight,
    titleMargin,
    areaMiles,
  } = classes;

  if (coords) {
    const { radius, position } = coords;
    const circleOptions = getPolygonOptions(mapType, isFocused);

    function radiusHandler() {
      if (!instance) {
        setInstance(this);
      }

      setRadius(this.getRadius());
    }

    function getCircleBoxPosition()  {
      const position = instance.getCenter();

      return {
        lat: position.lat() + convertMetersToLatitude(radius),
        lng: position.lng()
      }
    }

    function getMeasurementBox(radius) {
      const onlyTitle = !isMeasuringVisible && isTitleVisible;
      const onlyMeasuring = isMeasuringVisible && !isTitleVisible;
      let className = commonMeasuringBox;

      if (onlyTitle) {
        className += ` ${commonPosition}`;
      }

      if (onlyMeasuring) {
        className += ` ${boxWrapperWithMeasuring}`;
      }

      const titleClassName = onlyTitle
        ? titleFontWeight
        : `${titleFontWeight} ${titleMargin}`;

      return (
        <div className={className}>
          {isTitleVisible ? <div className={titleClassName}>{description}</div> : null}
          {isMeasuringVisible
            ? <>
              <div>Radius: {getMilesFromMeters(radius)} mi</div>
              <div>Diameter: {getDiameterFromRadius(radius)} mi</div>
              <div>Circumference: {getCircumferenceFromRadius(radius)} mi</div>
              <div>Area: {getAreaFromRadius(radius)} mi<span className={areaMiles}>2</span></div>
            </>
            : null
          }

        </div>
      );
    }

    function onLoadInfoBox(infoBox) {
      infoBox.boxClass = infoBox.boxClass + ' infoBoxContainer';
    }

    const fillColor = isHighlighted ? '#fff700' : isFocused || isEditing ? '#ffa300' : (shape.color ?? circleOptions.fillColor);
    const fillOpacity = isHighlighted || isFocused || isEditing ? 0.3 : (shape.fillOpacity ?? circleOptions.fillOpacity);
    const strokeColor = isHighlighted ? '#fff700' : isFocused || isEditing ? '#ffa300' : (shape.color ?? circleOptions.strokeColor);
    const strokeWeight = (isHighlighted || isFocused ? 10 : 3) * shape.size;

    return (
      <div>
        <Circle
          onLoad={onLoad}
          onUnmount={onUnmount}
          radius={radius}
          center={position}
          editable={isEditing}
          draggable={isEditing}
          options={{ ...circleOptions, clickable: !otherObjectIsEditing, fillColor, fillOpacity, strokeColor, strokeWeight }}
          visible={visible}
          onRadiusChanged={radiusHandler}
          onClick={onClick}
        />
        {radius && instance && !isEditing
          ? <InfoBox
              position={getCircleBoxPosition()}
              onLoad={onLoadInfoBox}
              visible={(isTitleVisible && visible) || (isMeasuringVisible && visible)}
              options={{
                disableAutoPan: true
              }}
            >
              {getMeasurementBox(radius)}
            </InfoBox>
          : null}
      </div>
    )
  } else {
    return null;
  }
}

function Line({
  coords,
  isEditing,
  isFocused,
  isHighlighted,
  onLoad,
  onUnmount,
  visible,
  onClick,
  color,
  size,
  description,
  isTitleVisible,
}) {
  const classes = useStyles();
  const { labelBox, commonPosition } = classes;

  // set the infobox at the highest latitude position
  function getInfoBoxPosition(a, b) {
    return a.lat > b.lat ? a : b;
  }

  function onLoadInfoBox(infoBox) {
    infoBox.boxClass = infoBox.boxClass + ' infoBoxContainer';
  }

  return (
    <>
      <Polyline
        onLoad={onLoad}
        onUnmount={onUnmount}
        path={coords || []}
        editable={isEditing}
        draggable={isEditing}
        options={{
          strokeColor: isHighlighted ? '#fff700' : isFocused || isEditing ? '#ffa300' : color,
          strokeWeight: (isHighlighted || isFocused || isEditing ? 5 : 3) * size,
          zIndex: 1000
        }}
        onClick={onClick}
        visible={visible}
      />
      {
        isTitleVisible !== undefined && coords && !isEditing
          ? (<InfoBox
            position={coords.reduce(getInfoBoxPosition)}
            visible={isTitleVisible && visible}
            onLoad={onLoadInfoBox}
            options={{
              disableAutoPan: true
            }}
          >
            <div className={`${labelBox} ${commonPosition}`}>{description}</div>
          </InfoBox>)
          : null
      }
    </>
  )
}
const iconPath = 'M0-48c-9.8 0-17.7 7.8-17.7 17.4 0 15.5 17.7 30.6 17.7 30.6s17.7-15.4 17.7-30.6c0-9.6-7.9-17.4-17.7-17.4z';
const circlePath = ' M-6,-30a6,6 0 1,0 12,0a6,6 0 1,0 -12,0';

function Pin({
  position,
  isEditing,
  isFocused,
  isHighlighted,
  onLoad,
  onUnmount,
  visible,
  onClick,
  color,
  size,
  description,
  isTitleVisible,
  otherObjectIsEditing,
}) {
  const infoBoxRef = useRef(null);
  const classes = useStyles();
  const { labelBox, pinLabelBox } = classes;
  const scale = (isHighlighted || isFocused || isEditing ? 0.9 : 0.7) * size;
  const infoBoxOptions = useMemo(() => {
    return { 
      disableAutoPan: true,
      pixelOffset: { height: -(scale * 50 - 35), width: 0 }
    }
  }, [scale]);

  useEffect(() => {
    if (!infoBoxRef.current) return;
    infoBoxRef.current.setOptions(infoBoxOptions);
  }, [infoBoxOptions]);

  function onLoadInfoBox(infoBox) {
    infoBoxRef.current = infoBox;
    infoBox.boxClass = infoBox.boxClass + ' infoBoxContainer';
  }

  return <>
    <Marker
      onLoad={onLoad}
      onUnmount={onUnmount}
      position={position}
      clickable={!otherObjectIsEditing}
      draggable={isEditing}
      icon={{
        path: iconPath + (isHighlighted || isFocused || isEditing ? circlePath : ''),
        fillColor: isHighlighted ? '#fff700' : isFocused || isEditing ? '#ffa300' : color,
        fillOpacity: 1,
        anchor: new window.google.maps.Point(0, 0),
        strokeWeight: isHighlighted || isFocused || isEditing ? 3.0 : 1.5,
        strokeColor: '#fff',
        scale
      }}
      onClick={onClick}
      visible={visible}
    />
    {
      isTitleVisible !== undefined && position && !isEditing
        ? (<InfoBox
          position={position}
          visible={isTitleVisible && visible}
          onLoad={onLoadInfoBox}
          options={infoBoxOptions}
        >
          <div className={`${labelBox} ${pinLabelBox}`}>{description}</div>
        </InfoBox>)
        : null
    }
  </>
}

const Label = React.memo(function ({
  data,
  textValue,
  color,
  size,
  autoSizeValue,
  fontWeight,
  backgroundColor,
  isEditing,
  otherObjectIsEditing,
  visible,
  isFocused,
  isHighlighted,
  objectId,
  isFontDependsFromZoom,
  onClick,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const zoom = useSelector(state => state.exploreMap.zoom);

  function handleAutoSizeChange(value) {
    dispatch(updateExploreObjectById(objectId, { autoSizeValue: value }, true));
  }

  useEffect(() => {
    if (isFontDependsFromZoom) {
      switch (zoom) {
        case 5:
          handleAutoSizeChange(13);
          break;
        case 6:
          handleAutoSizeChange(15);
          break;
        case 7:
          handleAutoSizeChange(17);
          break;
        case 8:
          handleAutoSizeChange(19);
          break;
        case 9:
          handleAutoSizeChange(21);
          break;
        case 10:
          handleAutoSizeChange(23);
          break;
        case 11:
          handleAutoSizeChange(25);
          break;
        default:
          if (zoom > 11) {
            handleAutoSizeChange(25);
          }
      }
    }
  }, [zoom, isFontDependsFromZoom]);

  const style = {
    fontWeight,
    backgroundColor,
    color,
    fontSize: isFontDependsFromZoom ? autoSizeValue : size,
    cursor: otherObjectIsEditing ? 'default' : 'pointer'
  }

  if (isHighlighted) {
    style.border = '0.4rem solid #fff700';
  }
  else if (isFocused) {
    style.border = '0.4rem solid #ffa300';
  }

  function onLoadInfoBox(infoBox) {
    infoBox.boxClass = infoBox.boxClass + ' infoBoxContainer';
  }

  if (data.lat == null || data.lng == null) return null;

  return (
    <InfoBox
      position={data}
      visible={visible && !isEditing}
      onLoad={onLoadInfoBox}
      options={{
        disableAutoPan: true
      }}
    >
      <div style={style} className={classes.labelWrapper} onClick={onClick}>{textValue}</div>
    </InfoBox>
  );
}, areEqual);

// function withTooltip(WrappedComponent) {
//   return function({ position, ...rest }) {
//     const [isHovered, setIsHovered] = useState(false);

//     return <>
//       <WrappedComponent setIsHovered={setIsHovered} {...rest} />
//       {isHovered &&
//         <InfoBox
//           position={position}
//           options={{
//             closeBoxURL: '',
//             alignBottom: true,
//             pixelOffset: {
//               width: 0,
//               height: -(!hasPinDisplayType && isProperty ? dotSize : height) * scale
//             },
//             boxStyle: { ...popupContainerStyles, overflow: 'initial' },
//             disableAutoPan: true
//           }}
//         >
//           <div className={classes.popupBubbleAnchor}>
//             <div className={classes.popupBubble}>
//               {tooltip}
//             </div>
//           </div>
//         </InfoBox>
//       }
//     </>
//   }
// }

function getMapComponent(
  object,
  focusedObjectId,
  highlightedObjectId,
  setExploreFocusedObjectId,
  mapType,
  isEditing,
  otherObjectIsEditing,
  addToObjectRefs,
  removeFromObjectRefs,
  mapRef,
  setRadius,
  manualRadius
) {
  const isFocused = object.id === focusedObjectId;
  const isHighlighted = object.id === highlightedObjectId;
  const onClick = otherObjectIsEditing ? () => {} : () => setExploreFocusedObjectId(isFocused ? null : object.id);
  const onLoad = ref => addToObjectRefs(object.id, ref)
  const onUnmount = ref => removeFromObjectRefs(object.id);
  switch (object.type) {
    case Constants.ViewsObjectType.LINE:
      return <Line
        key={object.id}
        objectId={object.id}
        coords={object.data}
        isEditing={isEditing}
        otherObjectIsEditing={otherObjectIsEditing}
        isFocused={isFocused}
        isHighlighted={isHighlighted}
        onLoad={onLoad}
        onUnmount={onUnmount}
        visible={object.visible}
        onClick={onClick}
        color={object.color}
        size={object.size}
        isTitleVisible={object.isTitleVisible}
        description={object.description}
      />
    case Constants.ViewsObjectType.PIN:
      return <Pin
        key={object.id}
        objectId={object.id}
        position={object.position}
        isEditing={isEditing}
        otherObjectIsEditing={otherObjectIsEditing}
        isFocused={isFocused}
        isHighlighted={isHighlighted}
        onLoad={onLoad}
        onUnmount={onUnmount}
        visible={object.visible}
        onClick={onClick}
        tooltip={object.description}
        color={object.color}
        size={object.size}
        isTitleVisible={object.isTitleVisible}
        description={object.description}
      />
    case Constants.ViewsObjectType.SHAPE:
      return <CustomShape
        key={object.id}
        objectId={object.id}
        description={object.description}
        isTitleVisible={object.isTitleVisible}
        shape={{ coords: object.data , color: object.color, fillOpacity: object.fillOpacity, size: object.size }}
        isEditing={isEditing}
        otherObjectIsEditing={otherObjectIsEditing}
        isFocused={isFocused}
        isHighlighted={isHighlighted}
        onLoad={onLoad}
        onUnmount={onUnmount}
        visible={object.visible}
        onClick={onClick}
        mapType={mapType}
      />
    case Constants.ViewsObjectType.CIRCLE:
      return <CircleShape
        key={object.id}
        objectId={object.id}
        shape={{ coords: object.data , color: object.color, fillOpacity: object.fillOpacity, size: object.size }}
        isEditing={isEditing}
        otherObjectIsEditing={otherObjectIsEditing}
        isFocused={isFocused}
        isHighlighted={isHighlighted}
        onLoad={onLoad}
        onUnmount={onUnmount}
        visible={object.visible}
        onClick={onClick}
        mapType={mapType}
        setRadius={setRadius}
        manualRadius={manualRadius}
        isTitleVisible={object.isTitleVisible}
        isMeasuringVisible={object.isMeasuringVisible}
        description={object.description}
      />
    case Constants.ViewsObjectType.PROPERTYDATA: // Property Data geo filter shares CustomShape
      return <CustomShape
        key={object.id}
        objectId={object.id}
        shape={{ coords: object.filterGeo, color: object.color, size: 1 }}
        isEditing={isEditing}
        otherObjectIsEditing={otherObjectIsEditing}
        isFocused={isFocused}
        isHighlighted={isHighlighted}
        onLoad={onLoad}
        onUnmount={onUnmount}
        visible={object.visible}
        onClick={onClick}
        mapType={mapType}
        isTitleVisible={object.isTitleVisible}
      />
    case Constants.ViewsObjectType.DISTANCEMEASURING:
      return <DistanceManager
        key={object.id}
        object={object}
        color={object.color[mapType]}
        borderColor={object.borderColor[mapType]}
        size={object.size}
        data={object.data || {}}
        mapRef={mapRef}
        mapType={mapType}
        isEditing={isEditing}
        otherObjectIsEditing={otherObjectIsEditing}
        isFocused={isFocused}
        isHighlighted={isHighlighted}
        visible={object.visible}
        onClick={onClick}
        isTitleVisible={object.isTitleVisible}
        isMeasuringVisible={object.isMeasuringVisible}
        description={object.description}
      />
    case Constants.ViewsObjectType.LABEL:
      return <Label
        key={object.id}
        objectId={object.id}
        color={object.fontColor}
        backgroundColor={object.labelBackgroundColor}
        size={object.size}
        autoSizeValue={object.autoSizeValue}
        data={object.data || {}}
        textValue={object.textValue}
        fontWeight={object.fontWeight}
        isEditing={isEditing}
        otherObjectIsEditing={otherObjectIsEditing}
        visible={object.visible}
        isFocused={isFocused}
        isHighlighted={isHighlighted}
        isFontDependsFromZoom={object.isFontDependsFromZoom}
        onClick={onClick}
      />
    default:
      return null;
  }
}

const objectRefs = {};
function ViewObjectManager({
  mapRef,
  selectedView,
  focusedObjectId,
  highlightedObjectId,
  setExploreFocusedObjectId,
  mapType,
  activeDrawingObject,
  activeEditingObject,
  setExploreActiveEditingObject,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  manualRadius,
  appNotify
}) {
  const hasPropertyData = useRef(false);
  const hasMarkets = useRef(false);

  useEffect(() => {
    if (activeEditingObject?.isCancelling) {
      const { id, type, drawingMode } = activeEditingObject;
      const obj = selectedView.objects.find(el => el.id === id);
      const data = reconstructDataForObject(type, drawingMode, obj);
      updateExploreObjectById(id, data, true);
      setExploreActiveEditingObject(null);
    } else if (
      activeEditingObject?.isSaving
      && activeEditingObject?.type !== 'DistanceMeasuring'
      && activeEditingObject?.type !== 'Label'
    ) {
      const { id, type, drawingMode } = activeEditingObject;
      const ref = objectRefs[id];
      const geo = getGeoFromOverlay(type, { type: drawingMode, overlay: ref})
      if (geo.filterGeo) {
        if (geo.isOversize) {
          const SqMetersToSqMiles = (squareMeters) => {
            const sqMiles = squareMeters * 0.00000038610215855;
            return formatValue(sqMiles, { type: 'fixed', decimals: 0, useSeparator: true })
          }
          appNotify(`Area exceeds limit of ${SqMetersToSqMiles(Constants.ViewsPropertyDataGeoFilterAreaLimit)} square miles. Please lower the shape size and try again.`, 'error');
          return;
        }
        setExploreGeoFilter(id, geo.filterGeo);
      } else {
        updateExploreObjectById(id, geo);
      }
      setExploreActiveEditingObject(null);
    }
  }, [activeEditingObject]);

  const addToObjectRefs = (id, ref) => {
    objectRefs[id] = ref;
  }
  const removeFromObjectRefs = (id) => {
    delete objectRefs[id];
  }
  const objs = useMemo(() => {
    if (!selectedView) return null
    return selectedView.objects.map(object => {
      const isEditing = object.id === activeEditingObject?.id;
      const otherObjectIsDrawing = activeDrawingObject != null && activeDrawingObject.id !== object.id;
      const otherObjectIsEditing = activeEditingObject != null && activeEditingObject.id !== object.id;
      if (object.type === Constants.ViewsObjectType.PROPERTYDATA) hasPropertyData.current = true;
      if (object.type === Constants.ViewsObjectType.MARKETS) hasMarkets.current = true;
      return getMapComponent(
        object,
        focusedObjectId,
        highlightedObjectId,
        setExploreFocusedObjectId,
        mapType,
        isEditing,
        otherObjectIsDrawing || otherObjectIsEditing,
        addToObjectRefs,
        removeFromObjectRefs,
        mapRef,
        setRadius,
        manualRadius
      );
    })
  }, [selectedView, focusedObjectId, highlightedObjectId, activeDrawingObject, activeEditingObject, mapType, manualRadius]);

  return (
    <>
      {objs}
    </>
  )
}

function mapState({ exploreViews, exploreMap }) {
  return {
    selectedView: exploreViews.selectedView,
    focusedObjectId: exploreViews.focusedObjectId,
    highlightedObjectId: exploreViews.highlightedObjectId,
    mapType: exploreMap.mapType,
    activeDrawingObject: exploreViews.activeDrawingObject,
    activeEditingObject: exploreViews.activeEditingObject,
    manualRadius: exploreViews.manualRadius,
  };
}

const mapDispatch = {
  setExploreFocusedObjectId,
  setExploreActiveEditingObject,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  appNotify
}

export default compose(
  connect(mapState, mapDispatch)
)(ViewObjectManager);
